// pages/UserAdmin/crm/BulkActionBar.js
//
// Barra flutuante escura no rodapé quando ≥1 contato está selecionado.
// Dispara ações em lote pelo endpoint POST /contacts/bulk (unificado).
//
// UX (out/26, Claude Design "CRM Moderno"):
//   - Aparece com opacidade + subida curta quando `selectedCount > 0`.
//   - Contador verde à esquerda (o mesmo verde da seleção na lista).
//   - Ações separadas por divisor vertical + "Excluir" em vermelho no
//     final (destrutiva).
//   - Botão redondo (X) limpa a seleção sem executar nada.
//   - Vidro escuro nos DOIS temas: a barra fica por cima da página e
//     precisa se destacar dela, clara ou escura. Por isso as cores aqui
//     são fixas, não os tokens do tema.
//
// RBAC: `assign_agent`, `delete` e `enviar template` só aparecem pra
// SUPERVISOR/ADMIN. A regra é aplicada aqui pra evitar tela inconsistente —
// mas o backend também bloqueia (defesa em profundidade).
//
// "Enviar template" entrou no grupo restrito por ser a única ação da barra
// que gasta dinheiro: a Meta cobra cada template direto no cartão do
// cliente, e o teto diário por número vai de 1.000 (STARTER) a 5.000
// envios. O agente continua podendo mandar template um a um pelo chat, como
// sempre pôde — o que muda é o disparo para uma lista inteira de uma vez.

import { Box } from '@mui/material';
import { CRM_THEME } from './theme';
import { Icon } from './icons';

function BarAction({ icon: IconCmp, label, onClick, danger = false }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        height: 36, flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 1,
        px: 1.5,
        borderRadius: '10px',
        border: 0,
        bgcolor: 'transparent',
        color: danger ? '#f87171' : 'rgba(255,255,255,.8)',
        fontFamily: CRM_THEME.font.ui,
        fontSize: 14,
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background-color .15s, color .15s',
        '&:hover': {
          bgcolor: danger ? 'rgba(239,68,68,.15)' : 'rgba(255,255,255,.08)',
          color: danger ? '#fca5a5' : '#fff',
        },
        '&:focus-visible': { outline: '2px solid #00d67c', outlineOffset: 1 },
      }}
    >
      <IconCmp size={15} />
      {label}
    </Box>
  );
}

function BarDivider() {
  return <Box aria-hidden sx={{ width: '1px', height: 20, mx: 0.5, flexShrink: 0, bgcolor: 'rgba(255,255,255,.12)' }} />;
}

export default function BulkActionBar({
  selectedCount = 0,
  isSupervisor = false,
  onAddToFolder,
  onAttachLabels,
  onAssignAgent,
  onSetInterest,
  onQualify,
  onSendTemplate,
  onDelete,
  onClear,
}) {
  const show = selectedCount > 0;
  return (
    <Box
      role="toolbar"
      aria-label="Ações com os contatos selecionados"
      aria-hidden={!show}
      sx={{
        position: 'fixed',
        bottom: 28,
        // Centro da ÁREA de conteúdo, não da janela: onde a sidebar existe
        // (md+), o meio fica deslocado pela metade dela. No celular ela some,
        // e o deslocamento empurrava a barra pra fora da tela: as últimas
        // ações ("Enviar template", "Excluir") ficavam visíveis e
        // inalcançáveis.
        left: { xs: '50%', md: 'calc(50% + var(--sidebar-width, 260px) / 2)' },
        transform: show ? 'translateX(-50%)' : 'translateX(-50%) translateY(12px)',
        // A barra cresce com o número de ações. Sem teto ela estoura a
        // viewport estreita; com ele, a fila de ações rola dentro da
        // própria barra e nenhuma fica inacessível.
        maxWidth: { xs: 'calc(100vw - 24px)', md: 'calc(100vw - var(--sidebar-width, 260px) - 24px)' },
        zIndex: 1250,       // acima de AppSidebar (que fica em ~1200)
        opacity: show ? 1 : 0,
        pointerEvents: show ? 'auto' : 'none',
        visibility: show ? 'visible' : 'hidden',
        transition: show
          ? 'opacity .2s ease, transform .25s ease, visibility 0s'
          : 'opacity .2s ease, transform .25s ease, visibility 0s .25s',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          p: '7px',
          borderRadius: `${CRM_THEME.radius.lg}px`,
          bgcolor: 'rgba(14,21,37,.92)',
          backdropFilter: 'blur(20px) saturate(140%)',
          WebkitBackdropFilter: 'blur(20px) saturate(140%)',
          border: '1px solid rgba(255,255,255,.10)',
          boxShadow: CRM_THEME.shadow.dark,
          overflowX: 'auto',
          // Rolagem sem barra visível: a barra de 4px em cima do vidro
          // escuro só sujaria. O arraste continua funcionando.
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          component="span"
          aria-live="polite"
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1, flexShrink: 0,
            height: 36, pl: 1.5, pr: 1.75,
            borderRadius: '12px',
            bgcolor: 'rgba(0,214,124,.14)',
            color: '#5fd9a5',
            fontFamily: CRM_THEME.font.ui, fontSize: 14, fontWeight: 600,
            whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
          }}
        >
          <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#00d67c' }} />
          {selectedCount.toLocaleString('pt-BR')} selecionado{selectedCount === 1 ? '' : 's'}
        </Box>

        <BarDivider />

        <BarAction icon={Icon.Folder} label="Adicionar a pasta" onClick={onAddToFolder} />
        <BarAction icon={Icon.Tag}    label="Etiquetas"         onClick={onAttachLabels} />
        {isSupervisor && (
          <BarAction icon={Icon.User} label="Atribuir agente"   onClick={onAssignAgent} />
        )}
        <BarAction icon={Icon.Pin}    label="Interesse"         onClick={onSetInterest} />
        {/* Qualificar o lead (out/26, rastreamento PR 2): atendente também —
            quem conversa com o lead é ele. Venda fechada não vem em lote. */}
        {onQualify && <BarAction icon={Icon.Confirmed} label="Qualificar" onClick={onQualify} />}

        {isSupervisor && (
          <>
            <BarDivider />
            <BarAction icon={Icon.Send} label="Enviar template" onClick={onSendTemplate} />
            <BarAction icon={Icon.Trash} label="Excluir" onClick={onDelete} danger />
          </>
        )}

        <Box
          component="button"
          type="button"
          onClick={onClear}
          aria-label="Limpar seleção"
          sx={{
            width: 36, height: 36, flexShrink: 0, ml: '2px', p: 0,
            borderRadius: '999px',
            bgcolor: 'rgba(255,255,255,.08)',
            border: 0,
            color: 'rgba(255,255,255,.6)',
            cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background-color .15s, color .15s',
            '&:hover': { bgcolor: 'rgba(255,255,255,.16)', color: '#fff' },
            '&:focus-visible': { outline: '2px solid #00d67c', outlineOffset: 1 },
          }}
        >
          <Icon.Close size={14} strokeWidth={2.2} />
        </Box>
      </Box>
    </Box>
  );
}
